import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Download, Search, Phone } from "lucide-react";

const AUDIENCES = [
  { value: "parents_active",     label: "Facility Members · active enrolments" },
  { value: "parents_all",        label: "Facility Members · all" },
  { value: "members_active",     label: "Membership Subscribers · currently valid" },
  { value: "members_all",        label: "Membership Subscribers · all accounts" },
  { value: "hub_members_all",    label: "All verified sign-ups (any account)" },
  { value: "booking_guests_90d", label: "Booking guests · 90d" },
  { value: "booking_guests_all", label: "Booking guests · all-time" },
  { value: "all_contacts",       label: "Everyone (union)" },
];

/**
 * ContactsDirectory — browsable list of every phone-reachable contact per
 * audience segment. Powered by the existing campaign preview endpoint.
 * Admin can filter, search, and export as CSV for offline use.
 */
export default function AdminContactsDirectory() {
  const [audience, setAudience] = useState("all_contacts");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.post("/admin/whatsapp/bridge/campaigns/preview", {
        audience, custom_numbers: [], full: true,
      });
      setData(res);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [audience]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = (data?.sample || []).filter((r) =>
    !search
      || (r.name || "").toLowerCase().includes(search.toLowerCase())
      || (r.phone || "").includes(search)
  );

  const exportCsv = () => {
    const rows = data?.sample || [];
    if (!rows.length) { toast.error("Nothing to export"); return; }
    const header = "Name,Phone,Source\n";
    const body = rows.map((r) => `"${(r.name || "").replace(/"/g, '""')}","${r.phone}","${r.source || ""}"`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `contacts-${audience}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success(`Exported ${rows.length} contacts`);
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-2">// Contacts · phone directory</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter">Contacts Directory</h1>
        <p className="text-sm text-white/60 mt-2 max-w-2xl">
          Every phone-reachable person on the platform — parents, members, booking guests. Use for WhatsApp campaigns,
          reminder calls, or CSV export for offline outreach.
        </p>
      </div>

      {/* Audience picker */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="contacts-audience-picker">
        {AUDIENCES.map((a) => (
          <button
            key={a.value}
            onClick={() => setAudience(a.value)}
            data-testid={`contacts-aud-${a.value}`}
            className={`text-xs font-mono uppercase tracking-widest px-3 py-2 border ${
              audience === a.value ? "border-brand-orange bg-brand-orange/15 brand-orange" : "border-white/15 hover:border-white/40"
            }`}
          >{a.label}</button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="border border-white/12 bg-[#131A2B] p-4 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Users size={16} className="brand-orange" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Total unique contacts</div>
            <div className="font-display text-2xl font-black" data-testid="contacts-total">
              {loading ? "…" : (data?.count ?? 0)}
            </div>
            {data?.sources && (
              <div className="text-[11px] text-white/60 mt-0.5">
                {Object.entries(data.sources).map(([k, v]) => (
                  <span key={k} className="mr-3">
                    <b>{v}</b> {k.replace("_", " ")}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-2.5 text-white/40" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name / phone…"
            data-testid="contacts-search"
            className="bg-black/40 border border-white/15 pl-9 pr-3 py-2 text-sm min-w-[240px] focus:outline-none focus:border-brand-blue"
          />
        </div>
        <button
          onClick={exportCsv}
          data-testid="contacts-export-csv"
          className="text-xs font-mono uppercase tracking-widest bg-brand-blue text-white px-3 py-2 hover:opacity-90 flex items-center gap-1"
        >
          <Download size={12} /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="border border-white/12 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left font-mono uppercase text-[10px] tracking-widest text-white/50">
              <th className="p-3">Name</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Source</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="4" className="text-center py-10 text-white/40">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="4" className="text-center py-10 text-white/40">
                {search ? "No contacts match your search" : "No contacts in this segment yet"}
              </td></tr>
            )}
            {!loading && filtered.map((r, i) => (
              <tr key={r.phone + i} className="border-t border-white/8" data-testid={`contact-row-${i}`}>
                <td className="p-3">{r.name || <span className="text-white/40 italic">(no name)</span>}</td>
                <td className="p-3 font-mono">{r.phone}</td>
                <td className="p-3">
                  <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 ${
                    r.source === "parent" ? "bg-brand-orange/20 brand-orange" :
                    r.source === "member" ? "bg-brand-blue/20 brand-blue" :
                    "bg-white/10 text-white/70"
                  }`}>{r.source || "other"}</span>
                </td>
                <td className="p-3 text-right">
                  <a href={`tel:${r.phone}`} className="text-[11px] font-mono uppercase tracking-widest border border-white/20 px-2 py-1 hover:bg-white/10 inline-flex items-center gap-1">
                    <Phone size={11} /> Call
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.truncated && (
          <div className="p-3 text-center text-xs text-white/40 border-t border-white/8">
            List truncated to 100 rows on screen. Use <b>Export CSV</b> to get all {data.count} contacts.
          </div>
        )}
      </div>
    </div>
  );
}
